import {
  LEAGUES,
  formatNumber,
  plural,
  type LeagueKind,
  type LeagueView,
} from '@jungles/shared';
import { useApi } from '../lib/hooks';
import {
  BackLink,
  ErrorState,
  Header,
  JungleStrip,
  Loader,
  Screen,
  TabBar,
} from '../components/Chrome';

/**
 * A2 · Моя лига и Лига залов (SPEC §5).
 * Середина таблицы сворачивается: видно топ, соседей и зону вылета.
 */

const HEAD = 8;
const TAIL = 3;
const NEIGHBOURS = 1;

export function LeagueScreen({ kind }: { kind: LeagueKind }) {
  const path = kind === 'personal' ? '/api/arena/league' : '/api/arena/gym-league';
  const { data, error, loading, reload } = useApi<LeagueView>(path);

  const visibleIndexes = (view: LeagueView): Set<number> => {
    const keep = new Set<number>();
    view.rows.forEach((row, index) => {
      if (index < HEAD) keep.add(index);
      if (index >= view.rows.length - TAIL) keep.add(index);
      if (row.isMe) {
        for (let i = index - NEIGHBOURS; i <= index + NEIGHBOURS; i += 1) {
          if (i >= 0 && i < view.rows.length) keep.add(i);
        }
      }
    });
    return keep;
  };

  return (
    <Screen tabs>
      <Header />
      <BackLink to="/arena" label="Арена" />

      {loading ? <Loader /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data ? (
        <section className="card" style={{ marginTop: 6 }}>
          <div className="lg-hd">
            <img src={data.icon} alt="" />
            <div>
              <h2>{data.name}</h2>
              <div className="nt">
                {data.periodLabel}
                {kind === 'personal'
                  ? ` · до конца ${data.daysLeft} ${plural(data.daysLeft, 'день', 'дня', 'дней')}`
                  : ''}
              </div>
              {data.myRank ? (
                <span className="chip" style={{ marginTop: 6 }}>
                  {kind === 'personal'
                    ? `Вы ${data.myRank}-е из ${data.size}`
                    : `#${data.myRank} из ${data.size}`}
                </span>
              ) : (
                <span className="chip" style={{ marginTop: 6 }}>
                  Заработайте очки, чтобы попасть в группу
                </span>
              )}
            </div>
          </div>

          <div className="lg-steps" aria-label={`Лиги: текущая ${data.tier} из 10`}>
            {LEAGUES.map((league) => (
              <img
                key={league.tier}
                className={
                  league.tier === data.tier
                    ? 'cur'
                    : league.tier < data.tier
                      ? 'done'
                      : undefined
                }
                src={`/assets/leagues/${league.icon}.webp`}
                alt={league.name}
              />
            ))}
          </div>

          <p className="nt" style={{ margin: '4px 0 10px' }}>
            {data.rule}
          </p>

          {data.rows.length === 0 ? (
            <p className="empty">
              Группа соберётся, как только вы получите первые очки на этой неделе.
            </p>
          ) : null}

          {(() => {
            const keep = visibleIndexes(data);
            const output: JSX.Element[] = [];
            let skipped = false;

            data.rows.forEach((row, index) => {
              if (!keep.has(index)) {
                if (!skipped) {
                  output.push(
                    <div className="gap" key={`gap-${index}`} aria-hidden="true">
                      ···
                    </div>,
                  );
                  skipped = true;
                }
                return;
              }
              skipped = false;

              const previous = data.rows[index - 1];
              if (previous && previous.zone === 'up' && row.zone !== 'up') {
                output.push(
                  <div className="zone up" key={`zone-up-${index}`}>
                    Зона повышения
                  </div>,
                );
              }
              if (previous && previous.zone !== 'down' && row.zone === 'down') {
                output.push(
                  <div className="zone down" key={`zone-down-${index}`}>
                    Зона вылета
                  </div>,
                );
              }

              output.push(
                <div
                  className={`gym${row.isMe ? ' me' : ''}${row.zone === 'down' ? ' dn' : ''}`}
                  key={row.id}
                >
                  <span className={`rk${row.rank > 99 ? ' sm' : ''}`}>{row.rank}</span>
                  <div>
                    <b>{row.name}</b>
                    <small>
                      {row.subtitle}
                      {row.isMe && data.gapToNext ? (
                        <>
                          {row.subtitle ? ' · ' : ''}
                          до {row.rank - 1}-го места {data.gapToNext}
                          <img
                            className="pt-ic xs"
                            src="/assets/ui/points-diamond-bicep.webp"
                            alt=""
                          />
                        </>
                      ) : null}
                    </small>
                  </div>
                  <span className="pts">
                    {formatNumber(row.points)}
                    <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="" />
                  </span>
                </div>,
              );
            });

            return output;
          })()}
        </section>
      ) : null}

      <JungleStrip />
      <TabBar />
    </Screen>
  );
}
